<template>
    <div>
        <!-- Filters. These exist so "select all matching" stays a bounded action: on a large site
             the unfiltered set is far too big to select safely, but one filter typically brings it
             under the cap. They compose with the table's own search. -->
        <div class="mb-3 flex flex-wrap items-start gap-3">
            <!-- select2 rather than a native select: on the large sites this page exists for, the
                 zone and profile lists run to hundreds of entries and scrolling one to find a
                 known name is the slow part. select2 gives each a type-to-filter box. Clearing
                 a selection emits null, which `setFilter` normalises back to '' so it drops out
                 of `activeFilterParams` exactly as the old empty option did. -->
            <label class="flex w-48 flex-col text-sm text-gray-600">
                Area
                <select2 class="mt-1"
                         name="filter_area"
                         :options="filterAreas"
                         :multiple="false"
                         :close-on-select="true"
                         :allow-clear="true"
                         placeholder="All areas"
                         :value="filters.areaId"
                         @update:value="setFilter('areaId', $event)"
                ></select2>
            </label>

            <label class="flex w-48 flex-col text-sm text-gray-600">
                Zone
                <!-- Grouped by area, so this one needs select2-group to keep its optgroups. -->
                <select2-group class="mt-1"
                               name="filter_zone"
                               :options="zoneSelectByAreas"
                               :multiple="false"
                               :close-on-select="true"
                               :allow-clear="true"
                               placeholder="All zones"
                               :model-value="filters.zoneId"
                               @update:model-value="setFilter('zoneId', $event)"
                ></select2-group>
            </label>

            <label class="flex w-56 flex-col text-sm text-gray-600">
                Scaling Profile
                <select2 class="mt-1"
                         name="filter_scaling_profile"
                         :options="scalingProfileOptions"
                         :multiple="false"
                         :close-on-select="true"
                         :allow-clear="true"
                         :sort-by-text="true"
                         placeholder="All scaling profiles"
                         :value="filters.scalingProfileId"
                         @update:value="setFilter('scalingProfileId', $event)"
                ></select2>
            </label>

            <label class="flex w-56 flex-col text-sm text-gray-600">
                Alarm Profile
                <select2 class="mt-1"
                         name="filter_alarm_profile"
                         :options="alarmProfileOptions"
                         :multiple="false"
                         :close-on-select="true"
                         :allow-clear="true"
                         :sort-by-text="true"
                         placeholder="All alarm profiles"
                         :value="filters.alarmProfileId"
                         @update:value="setFilter('alarmProfileId', $event)"
                ></select2>
            </label>

            <!-- Matches a filter's box metrics exactly (same padding, text size and border) and
                 carries an empty label line, so it lines up with the selects rather than sitting
                 lower and shorter than them. -->
            <label v-if="hasActiveFilters" class="flex flex-col text-sm text-gray-600">
                <span aria-hidden="true">&nbsp;</span>
                <button type="button"
                        class="mt-1 rounded border border-gray-300 bg-white px-3 py-2 text-base leading-normal text-gray-700 hover:bg-gray-50"
                        @click="clearFilters">
                    Clear filters
                </button>
            </label>
        </div>

        <ajax-table :data="data"
                    :headers="headers"
                    v-model:loading="ajaxTable.loading"
                    v-model:sort-by="ajaxTable.sortBy"
                    v-model:sort-desc="ajaxTable.sortDesc"
                    v-model:page="ajaxTable.page"
                    v-model:page-info="ajaxTable.pageInfo"
                    v-model:search="ajaxTable.search"
                    v-model:items-per-page="ajaxTable.itemsPerPage"
                    :exportable="false"
                    :row-selectable="canEdit"
                    @rowSelected="onRowClicked"
                    @loadData="getData"
                    no-data-message="No points found"
                    loading-message="Loading points"
        >
            <!-- Own the items-per-page slot rather than restyling AjaxTable's default markup.
                 That default stacks its label above the select with the result count beneath, so
                 it never lines up with the single-line search; overriding the slot lets it be
                 laid out inline directly instead of fighting the component's internals. -->
            <template #items-per-page>
                <div class="flex flex-col items-end">
                    <label class="flex items-center gap-2 whitespace-nowrap text-gray-700">
                        Items Per Page
                        <select class="w-20 rounded border border-gray-300 bg-white px-3 py-2 text-base leading-normal text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                :value="ajaxTable.itemsPerPage"
                                @change="onItemsPerPageChange($event.target.value)">
                            <option v-for="size in itemsPerPageOptions" :key="size" :value="size">{{ size }}</option>
                        </select>
                    </label>

                    <div class="mt-1 whitespace-nowrap text-sm text-gray-500">
                        Showing {{ ajaxTable.pageInfo.from }} - {{ ajaxTable.pageInfo.to }} of
                        {{ ajaxTable.pageInfo.total }} results
                    </div>
                </div>
            </template>

            <!-- Bulk actions sit in the `search-right` slot, which AjaxTable renders in the same
                 flex row as the search box. Only rendered once something is selected: with
                 nothing ticked there is no action to take, so it would just be noise. -->
            <template #search-right>
                <!-- Select all / none for the rows on this page. Always visible, since it is how a
                     selection gets started; individual rows are still ticked in the grid. -->
                <div v-if="canEdit && canSelectAllMatching" class="js-actions-group ml-3 flex shrink-0 flex-col">
                    <button type="button"
                            :disabled="selectingAll"
                            class="flex items-center justify-center gap-2 whitespace-nowrap rounded border border-gray-300 bg-white px-4 py-2 text-base leading-normal text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            @click="onSelectAllClicked">
                        {{ selectAllLabel }}
                    </button>

                    <div class="mt-1 whitespace-nowrap text-sm text-gray-500">
                        {{ selectAllCaption }}
                    </div>
                </div>

                <!-- Count sits beneath the button as a caption, mirroring the "Showing N - N of
                     N results" line under the items-per-page select on the other side.
                     The group keeps its space when nothing is selected (invisible rather than
                     removed) so the header does not change height and nudge the table when the
                     first row is ticked. -->
                <div v-if="canEdit"
                     class="js-actions-group ml-3 flex shrink-0 flex-col"
                     :class="{'pointer-events-none invisible': selectedCount === 0}">
                    <div class="relative">
                        <button type="button"
                                class="flex items-center justify-center gap-2 rounded border border-gray-300 bg-white px-4 py-2 text-base leading-normal text-gray-700 hover:bg-gray-50"
                                @click="showActionsMenu = ! showActionsMenu">
                            Actions
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6"/></svg>
                        </button>

                        <!-- Full-screen catcher so a click anywhere else closes the menu. -->
                        <div v-if="showActionsMenu" class="fixed inset-0 z-10" @click="showActionsMenu = false"></div>

                        <div v-if="showActionsMenu"
                             class="absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-md border border-gray-200 bg-white shadow-lg">
                            <div class="px-4 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                                Apply to {{ selectedCount }} point{{ selectedCount === 1 ? '' : 's' }}
                            </div>

                            <button v-for="action in bulkActions"
                                    :key="action.field"
                                    type="button"
                                    class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    @click="showActionsMenu = false; openBulkPicker(action.field)">
                                {{ action.label }}
                            </button>

                            <div class="border-t border-gray-100">
                                <button type="button"
                                        class="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                                        @click="showActionsMenu = false; clearSelection()">
                                    Clear selection
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="mt-1 whitespace-nowrap text-sm text-gray-500">
                        {{ selectedCount }} selected
                    </div>
                </div>
            </template>

            <template v-for="slot in 6" :key="slot" #[`col-${slot}`]="props">
                <!-- Each visual column maps to a field via `headers`, so the layout is driven by
                     that array rather than hard-coded per slot. -->
                <template v-if="columnKey(slot) === 'name'">
                    <!-- Name and serial share a cell: the serial is a read-only identifier for the
                         point above it, so it reads as a subtitle rather than earning a column of
                         its own. The select box rides here too, because TailwindTable's grid is
                         uniform (every column 1fr) and a dedicated checkbox column would claim a
                         full ~17% of the table for a 16px control. -->
                    <!-- gap matches the cell's own horizontal padding (px-6 = 24px at sm+, px-4
                         below) so the checkbox sits with equal space either side. -->
                    <div class="flex w-full items-center gap-4 sm:gap-6"
                         :class="{
                             'js-row-selected': !! selected[props.row.id],
                             'js-row-changed': changedFieldCount(props.row) > 0,
                         }">
                        <input v-if="canEdit"
                               type="checkbox"
                               class="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                               :checked="!! selected[props.row.id]"
                               @click.stop
                               @change="toggleSelected(props.row, $event.target.checked)">

                        <!-- min-w-0 lets the input shrink inside the flex row; without it the
                             fixed-width input overflows and the name is clipped. -->
                        <div class="min-w-0 flex-1">
                            <!-- Serial sits above the name: it is the point's fixed identifier, so
                                 it stays put while the name below it can show an old -> new pair
                                 when renamed.

                                 The serial itself stays plain text so it can still be selected and
                                 copied by hand; the icons beside it are just shortcuts.

                                 Clicks are stopped on the whole group so reaching for the serial
                                 does not tick the row. The group is `w-fit`, so that covers the
                                 text and the two icons only -- the empty space to its right is
                                 still row, and clicking there selects as it does everywhere. -->
                            <div class="js-name-subtext group/serial flex w-fit max-w-full items-center gap-1"
                                 @click.stop>
                                <span class="truncate font-mono text-gray-600">{{ props.row.meter_serial }}</span>

                                <button type="button"
                                        class="ml-0.5 shrink-0 rounded p-0.5 text-gray-400 transition-opacity duration-75 hover:bg-gray-100 hover:text-gray-700 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        :class="copiedSerialId === props.row.id ? 'opacity-100' : 'opacity-0 group-hover/serial:opacity-100'"
                                        :title="copiedSerialId === props.row.id ? 'Copied' : 'Copy serial'"
                                        @click.stop="copySerial(props.row)">
                                    <span class="sr-only">Copy serial</span>

                                    <!-- Swaps to a tick for a moment after copying, so the click is
                                         acknowledged without a toast covering the table. -->
                                    <svg v-if="copiedSerialId === props.row.id" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-check text-green-600"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/></svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-copy"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z"/><path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1"/></svg>
                                </button>

                                <!-- Copies just the last four, which is how points are usually
                                     referred to in conversation. The badge carries the "4" so the
                                     two buttons are tellable apart without a hover. -->
                                <button type="button"
                                        class="relative shrink-0 rounded p-0.5 text-gray-400 transition-opacity duration-75 hover:bg-gray-100 hover:text-gray-700 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        :class="copiedLastFourId === props.row.id ? 'opacity-100' : 'opacity-0 group-hover/serial:opacity-100'"
                                        :title="copiedLastFourId === props.row.id ? 'Copied' : `Copy last 4 (${lastFour(props.row.meter_serial)})`"
                                        @click.stop="copyLastFour(props.row)">
                                    <span class="sr-only">Copy last four of serial</span>

                                    <svg v-if="copiedLastFourId === props.row.id" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-check text-green-600"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 12l5 5l10 -10"/></svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-copy"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z"/><path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1"/></svg>

                                    <span v-if="copiedLastFourId !== props.row.id"
                                          class="pointer-events-none absolute -bottom-0.5 -right-0.5 font-sans text-[9px] font-bold leading-none text-gray-500">4</span>
                                </button>
                            </div>

                            <!-- Click to edit, matching the value cells. The display button and the
                                 input carry identical box metrics (same padding, border width,
                                 font-size and line-height), so swapping between them does not
                                 shift the row. -->
                            <input v-if="editingNameId === props.row.id"
                                   :ref="el => setNameInput(props.row.id, el)"
                                   type="text"
                                   class="js-name-field mt-0.5 w-full rounded-md border border-blue-400 bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                   :value="valueFor(props.row, 'name')"
                                   @click.stop
                                   @input="setValue(props.row, 'name', $event.target.value)"
                                   @blur="stopEditingName(props.row.id)"
                                   @keydown.enter.prevent="stopEditingName(props.row.id)"
                                   @keydown.esc.prevent="stopEditingName(props.row.id)">

                            <button v-else
                                    type="button"
                                    class="js-name-field group mt-0.5 flex w-full items-center justify-between gap-2 rounded-md border border-dashed border-gray-300 text-left transition-colors duration-75 hover:border-solid hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none disabled:border-transparent"
                                    :class="isChanged(props.row, 'name') ? 'font-semibold text-amber-800' : 'text-gray-900'"
                                    :disabled="! canEdit"
                                    @click.stop="startEditingName(props.row)">
                                <span class="truncate">{{ valueFor(props.row, 'name') }}</span>
                                <svg class="h-4 w-4 shrink-0 text-blue-600 opacity-0 transition-opacity duration-75 group-hover:opacity-100"
                                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M12 20h9"/>
                                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                                </svg>
                            </button>

                            <!-- Previous name, struck through beneath the new one. Clicking it
                                 restores just the name. -->
                            <button v-if="isChanged(props.row, 'name')"
                                    type="button"
                                    class="js-name-subtext group/undo flex w-full items-center gap-1.5 rounded text-left text-sm text-gray-400 transition-colors duration-75 hover:text-gray-700"
                                    title="Click to restore this name"
                                    @click.stop="revertField(props.row, 'name')">
                                <span class="truncate line-through">{{ originalDisplayValue(props.row, 'name') }}</span>
                                <svg class="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity duration-75 group-hover/undo:opacity-100"
                                     xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M3 7v6h6"/>
                                    <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </template>

                <template v-else-if="columnKey(slot) === 'zone_id'">
                    <!-- Column, so the previous value stacks under the new one rather than
                         sitting beside it. min-w-0 lets both truncate. -->
                    <div class="min-w-0 w-full">
                        <!-- The whole cell is the click target. The pencil only appears on hover so
                             the grid stays clean at rest but the cell reads as editable under the
                             cursor. -->
                        <button type="button"
                                class="group flex w-full items-center justify-between gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-left transition-colors duration-75 hover:border-solid hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none disabled:border-transparent"
                                :class="isChanged(props.row, 'zone_id') ? 'font-semibold text-amber-800' : 'text-gray-700'"
                                :disabled="! canEdit"
                                @click.stop="openPicker('zone_id', props.row)">
                            <span class="truncate">{{ displayValue('zone_id', valueFor(props.row, 'zone_id'), props.row) }}</span>
                            <svg class="h-4 w-4 shrink-0 text-blue-600 opacity-0 transition-opacity duration-75 group-hover:opacity-100"
                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 20h9"/>
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                            </svg>
                        </button>

                        <!-- Previous value, struck through beneath the new one. Clicking it puts
                             the field back, so one field can be undone without discarding the rest of
                             the row's edits. -->
                        <button v-if="isChanged(props.row, 'zone_id')"
                                type="button"
                                class="group/undo flex w-full items-center gap-1.5 rounded px-3 text-left text-sm text-gray-400 transition-colors duration-75 hover:text-gray-700"
                                title="Click to restore this value"
                                @click.stop="revertField(props.row, 'zone_id')">
                            <span class="truncate line-through">{{ originalDisplayValue(props.row, 'zone_id') }}</span>
                            <svg class="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity duration-75 group-hover/undo:opacity-100"
                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M3 7v6h6"/>
                                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
                            </svg>
                        </button>
                    </div>
                </template>

                <template v-else-if="columnKey(slot) === 'scaling_profile_id'">
                    <!-- Column, so the previous value stacks under the new one rather than
                         sitting beside it. min-w-0 lets both truncate. -->
                    <div class="min-w-0 w-full">
                        <button type="button"
                                class="group flex w-full items-center justify-between gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-left transition-colors duration-75 hover:border-solid hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none disabled:border-transparent"
                                :class="isChanged(props.row, 'scaling_profile_id') ? 'font-semibold text-amber-800' : 'text-gray-700'"
                                :disabled="! canEdit"
                                @click.stop="openPicker('scaling_profile_id', props.row)">
                            <span class="truncate">{{ displayValue('scaling_profile_id', valueFor(props.row, 'scaling_profile_id'), props.row) }}</span>
                            <svg class="h-4 w-4 shrink-0 text-blue-600 opacity-0 transition-opacity duration-75 group-hover:opacity-100"
                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 20h9"/>
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                            </svg>
                        </button>

                        <!-- Previous value, struck through beneath the new one. Clicking it puts
                             the field back, so one field can be undone without discarding the rest of
                             the row's edits. -->
                        <button v-if="isChanged(props.row, 'scaling_profile_id')"
                                type="button"
                                class="group/undo flex w-full items-center gap-1.5 rounded px-3 text-left text-sm text-gray-400 transition-colors duration-75 hover:text-gray-700"
                                title="Click to restore this value"
                                @click.stop="revertField(props.row, 'scaling_profile_id')">
                            <span class="truncate line-through">{{ originalDisplayValue(props.row, 'scaling_profile_id') }}</span>
                            <svg class="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity duration-75 group-hover/undo:opacity-100"
                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M3 7v6h6"/>
                                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
                            </svg>
                        </button>
                    </div>
                </template>

                <template v-else-if="columnKey(slot) === 'selected_alarm_profile'">
                    <!-- Column, so the previous value stacks under the new one rather than
                         sitting beside it. min-w-0 lets both truncate. -->
                    <div class="min-w-0 w-full">
                        <button type="button"
                                class="group flex w-full items-center justify-between gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-left transition-colors duration-75 hover:border-solid hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:pointer-events-none disabled:border-transparent"
                                :class="isChanged(props.row, 'selected_alarm_profile') ? 'font-semibold text-amber-800' : 'text-gray-700'"
                                :disabled="! canEdit"
                                @click.stop="openPicker('selected_alarm_profile', props.row)">
                            <span class="min-w-0 flex-1">
                                <span class="block truncate">
                                    {{ alarmOptionFor(props.row, valueFor(props.row, 'selected_alarm_profile')).label }}
                                </span>

                                <!-- High / low threshold chips, matching the alarm snapshot page:
                                     red with an up arrow for the high end, blue with a down arrow
                                     for the low end, alarm and caution combined per end. -->
                                <!-- Only for a point that actually has a profile. The `(none)`
                                     fallback carries no enabled flags, so rendering it would put
                                     red "disabled" bells under a point whose alarm profile does
                                     not exist -- indistinguishable from a real profile with its
                                     alarms switched off. -->
                                <alarm-threshold-chips
                                    v-if="hasAlarmProfile(props.row)"
                                    v-bind="alarmChipProps(alarmOptionFor(props.row, valueFor(props.row, 'selected_alarm_profile')))"
                                ></alarm-threshold-chips>
                            </span>

                            <svg class="h-4 w-4 shrink-0 text-blue-600 opacity-0 transition-opacity duration-75 group-hover:opacity-100"
                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 20h9"/>
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                            </svg>
                        </button>

                        <!-- Previous value, struck through beneath the new one. Clicking it puts
                             the field back, so one field can be undone without discarding the rest of
                             the row's edits. -->
                        <button v-if="isChanged(props.row, 'selected_alarm_profile')"
                                type="button"
                                class="group/undo flex w-full items-center gap-1.5 rounded px-3 text-left text-sm text-gray-400 transition-colors duration-75 hover:text-gray-700"
                                title="Click to restore this value"
                                @click.stop="revertField(props.row, 'selected_alarm_profile')">
                            <span class="truncate line-through">{{ originalDisplayValue(props.row, 'selected_alarm_profile') }}</span>
                            <svg class="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity duration-75 group-hover/undo:opacity-100"
                                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M3 7v6h6"/>
                                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
                            </svg>
                        </button>
                    </div>
                </template>
            </template>

        </ajax-table>

        <!-- Persistent save bar. Edits are held in memory across paging, so the footer is the
             running tally of what will actually be written. -->
        <!-- `2xl:w-3/4` matches the page's content column (and the System Settings save bar), so
             the footer's message and actions line up with the table's edges. -->
        <!-- `align-to` points the footer's inner row at the content column, so the actions end
             exactly where the table does. A max-width cap cannot express that here: the column is
             sized with a percentage (2xl:w-3/4) inside a gutter-padded container, so its real edges
             depend on both the sidebar offset and the breakpoint. Measuring the element is the only
             thing that stays correct across widths. -->
        <action-footer v-if="canEdit" align-to="#edit-multiple-points">
            <template #message>
                <span v-if="dirtyCount === 0">No unsaved changes.</span>
                <span v-else-if="overSaveLimit" class="font-medium text-red-700">
                    {{ dirtyCount }} points changed. Only {{ selectAllLimit }} can be saved at a time, so undo some changes before saving.
                </span>
                <span v-else-if="blankNameCount > 0" class="font-medium text-red-700">
                    {{ blankNameCount }} point{{ blankNameCount === 1 ? ' has' : 's have' }} a blank name. Enter a name or undo the rename before saving.
                </span>
                <span v-else class="font-medium text-gray-700">
                    {{ dirtyCount }} point{{ dirtyCount === 1 ? '' : 's' }} changed but not yet saved.
                </span>
            </template>

            <template #actions>
                <button type="button"
                        :disabled="saving || dirtyCount === 0"
                        class="relative inline-flex min-h-[48px] items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:min-w-48"
                        @click="discardChanges">
                    Discard
                </button>

                <button type="button"
                        :disabled="saving || saveBlocked"
                        class="relative inline-flex min-h-[48px] items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 hover:bg-blue-500 focus:border-blue-700 focus:outline-none active:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600 sm:min-h-0 sm:min-w-48"
                        @click="reviewChangesBeforeSave">
                    <span v-if="saving" class="lnr icon-loading animate-spin mr-3"></span>
                    {{ saveLabel }}
                </button>
            </template>
        </action-footer>

        <!-- Review step. A bulk write can touch points the user can no longer see, so every
             pending change is listed before anything is committed. -->
        <app-modal v-model:open="showReview"
                   title="Review changes before saving"
                   max-width="max-w-3xl"
                   persistent
                   :primary-label="saving ? 'Saving...' : `Save ${dirtyCount} point${dirtyCount === 1 ? '' : 's'}`"
                   :primary-disabled="saving || ! reviewConfirmed"
                   :primary-variant="needsTypedConfirm ? 'danger' : 'primary'"
                   cancel-label="Keep editing"
                   @submit="save"
                   @cancel="showReview = false"
        >
            <p class="mb-4 text-sm text-gray-600">
                You're about to update
                <span class="font-semibold text-gray-900">{{ dirtyCount }}</span>
                point<span v-if="dirtyCount !== 1">s</span>
                ({{ reviewFieldCount }} field<span v-if="reviewFieldCount !== 1">s</span>).
                This cannot be undone.
            </p>

            <!-- The size of each change, before the row-by-row list. 250 rows that all say the same
                 thing hide how big the change is; one line per distinct change does not. -->
            <ul class="mb-4 divide-y divide-gray-100 rounded-md border border-gray-200 px-3 text-sm">
                <li v-for="item in reviewSummary"
                    :key="item.key"
                    class="flex items-baseline justify-between gap-3 py-2">
                    <span class="text-gray-700">
                        {{ item.label }}
                        <template v-if="item.to !== null">
                            <span aria-hidden="true">&rarr;</span>
                            <span class="font-medium text-gray-900">{{ item.to }}</span>
                        </template>
                    </span>
                    <span class="shrink-0 font-semibold text-gray-900">
                        {{ item.count }} point{{ item.count === 1 ? '' : 's' }}
                    </span>
                </li>
            </ul>

            <banner-warning v-if="highImpactCount > 0 && ! needsTypedConfirm" class="mb-4">
                <template #title>
                    {{ highImpactCount }} point{{ highImpactCount === 1 ? '' : 's' }} will get a settings update sent to the device.
                </template>
            </banner-warning>

            <!-- A wrong scaling or alarm profile across a "select all" is the most damaging mistake
                 this page allows. Typing the number makes the user read it; the server checks the
                 same number (EditMultiplePointsController::TYPED_CONFIRM_ABOVE). -->
            <banner-error v-if="needsTypedConfirm" class="mb-4">
                <template #title>
                    This changes the scaling or alarm profile on {{ highImpactCount }} points.
                </template>
                <template #content>
                    <p>Each device gets a settings update. Type <span class="font-semibold">{{ highImpactCount }}</span> to confirm.</p>
                    <input v-model="confirmCount"
                           type="text"
                           inputmode="numeric"
                           autocomplete="off"
                           aria-label="Number of points to confirm"
                           class="mt-2 w-32 rounded-md border border-red-300 bg-white text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500">
                </template>
            </banner-error>

            <div class="max-h-96 overflow-y-auto">
                <table class="w-full text-left text-sm">
                    <thead class="sticky top-0 border-b border-gray-200 bg-white text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                            <th class="px-0 py-2 pr-3 font-medium">Point</th>
                            <th class="px-3 py-2 font-medium">Field</th>
                            <th class="px-3 py-2 font-medium">From</th>
                            <th class="px-3 py-2 font-medium">To</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <template v-for="row in reviewChanges" :key="row.id">
                            <tr v-for="(change, index) in row.fields" :key="`${row.id}-${change.field}`">
                                <td class="px-0 py-2 pr-3 align-top">
                                    <div v-if="index === 0">
                                        <div class="font-medium text-gray-900">{{ row.name }}</div>
                                        <div class="text-xs text-gray-500">{{ row.serial }}</div>
                                    </div>
                                </td>
                                <td class="px-3 py-2 align-top text-gray-700">{{ change.label }}</td>
                                <td class="px-3 py-2 align-top text-gray-500 line-through">{{ change.from }}</td>
                                <td class="px-3 py-2 align-top font-medium text-gray-900">{{ change.to }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </app-modal>

        <!-- Variant C: focused single-field picker, opened straight from the cell. -->
        <quick-pick-modal :open="showPicker"
                          :title="pickerTitle"
                          :subtitle="pickerSubtitle"
                          :placeholder="pickerPlaceholder"
                          :options="pickerOptions"
                          :current-value="pickerCurrentValue"
                          :allow-clear="pickerAllowsClear"
                          @select="applyPick"
                          @update:open="onPickerOpenChange"
        ></quick-pick-modal>
    </div>
</template>

<script>
import axios from 'axios';
import {ajaxTableMixin} from '@/mixins/ajaxTableMixin';
import {companyMixin} from '@/mixins/companyMixin';
import ActionFooter from '@/components/elements/ActionFooter.vue';
import AjaxTable from '@/components/elements/AjaxTable.vue';
import AlarmThresholdChips from '@/components/Meters/AlarmThresholdChips.vue';
import AppModal from '@/components/elements/AppModal.vue';
import BannerError from '@/components/Banners/BannerError.vue';
import BannerWarning from '@/components/Banners/BannerWarning.vue';
import QuickPickModal from '@/components/Meters/QuickPickModal.vue';
import select2 from '@/components/elements/select2.vue';
import select2Group from '@/components/elements/select2-group.vue';

export default {
    name: 'EditMultiplePoints',
    mixins: [ajaxTableMixin, companyMixin],
    components: {
        ActionFooter,
        AjaxTable,
        AlarmThresholdChips,
        AppModal,
        BannerError,
        BannerWarning,
        QuickPickModal,
        select2,
        'select2-group': select2Group,
    },
    props: {
        zoneSelectByAreas: {
            type: Object,
            default: () => ({}),
        },
        scalingProfiles: {
            type: Object,
            default: () => ({}),
        },
        filterAreas: {
            type: Object,
            default: () => ({}),
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        postUrl: {
            type: String,
            required: true,
        },
        typedConfirmAbove: {
            type: Number,
            default: 10,
        },
    },
    data() {
        return {
            data: [],
            alarmProfiles: {},
            saving: false,
            requestNumber: 0,

            // Rows are discarded when the user pages/searches/sorts, so pending edits are held here
            // (keyed by point id) rather than in the DOM. This is also what lets Save post only the
            // points that actually changed instead of every point on the site (ENGS-658).
            dirty: {},

            // Server-side values as they were when a point was first edited, so the review modal
            // can render a before -> after diff for rows that are no longer on screen.
            originals: {},

            // Review-before-save modal.
            showReview: false,
            // What the user has typed into the review's typed confirmation.
            confirmCount: '',

            // Variant C: one focused picker per cell, rather than a form for the whole row.
            showPicker: false,
            pickerField: null,
            pickerRowId: null,

            bulkActions: [
                {field: 'zone_id', label: 'Set zone'},
                {field: 'scaling_profile_id', label: 'Set scaling profile'},
                {field: 'selected_alarm_profile', label: 'Set alarm profile'},
            ],

            fieldLabels: {
                name: 'Name',
                zone_id: 'Area / Zone',
                scaling_profile_id: 'Scaling Profile',
                selected_alarm_profile: 'Alarm Profile',
            },

            // Row field -> the `meter[id][...]` key postEditMultiple reads for it.
            saveParamNames: {
                name: 'name',
                zone_id: 'zone_id',
                scaling_profile_id: 'scaling_profile_id',
                selected_alarm_profile: 'alarm_profile',
            },

            // Points ticked for a bulk apply, keyed by id. Scoped to the current page: a
            // selection is cleared on page/search/sort so "apply to selected" always means the
            // rows the user can actually see.
            selected: {},
            // Bumped whenever the selection is reset, so a "select all matching" response that
            // lands after the filters, search or page changed can tell it is stale and drop itself.
            selectionGeneration: 0,
            // The search the rows on screen (and so the "N matching" total) were loaded with.
            // `ajaxTable.search` is the live input, which runs ahead of the debounced fetch.
            loadedSearch: '',

            // True while the picker is applying one value across the whole selection.
            bulkMode: false,

            // Id of the point whose name is currently being edited inline (one at a time).
            editingNameId: null,
            // Points whose serial (or last four) was just copied, so the button can show a tick
            // briefly. Tracked separately so the two buttons acknowledge independently.
            copiedSerialId: null,
            copiedLastFourId: null,
            nameInputs: {},

            showActionsMenu: false,

            // Server-side filters. Narrowing the set is what makes "select all matching" safe:
            // every point selected has to be loaded first so its untouched fields can be posted
            // back unchanged, and that is only bounded once a filter is applied.
            filters: {
                areaId: '',
                zoneId: '',
                scalingProfileId: '',
                alarmProfileId: '',
            },
            selectingAll: false,
            // How many points "Select all" may take on. The server owns this number (see
            // EditMultiplePointsController::SELECT_ALL_LIMIT) and returns it with every selectable()
            // response, so this is only the value used before the first lookup -- it is replaced by
            // the server's on first contact. Kept in step deliberately: if this were larger, the UI
            // would offer a Select all that the server then refuses.
            selectAllLimit: 250,
            // Total matching the current filters, from the last selectable() lookup.
            matchingTotal: null,
            // Deliberately capped at 500. An unlimited "All" was measured at ~5.4s to render and
            // ~2.3s per edit on a 5,000-point site -- the same "page has frozen" symptom this
            // screen was rewritten to fix. Search covers finding a point; the review modal covers
            // seeing every pending change.
            itemsPerPageOptions: ['10', '25', '50', '75', '100', '250', '500'],
        };
    },
    computed: {
        // Both profile lists arrive keyed by id. Iterating that object directly puts them in
        // ascending *id* order however the server sorted them, because JavaScript walks integer-like
        // object keys numerically -- so the list is rebuilt as an array here and sorted by label.
        sortedScalingProfiles() {
            return Object.entries(this.scalingProfiles)
                .map(([id, label]) => ({id, label}))
                .sort((a, b) => a.label.localeCompare(b.label));
        },
        sortedAlarmProfiles() {
            return Object.entries(this.alarmProfiles)
                .map(([id, option]) => ({id, label: option.label}))
                .sort((a, b) => a.label.localeCompare(b.label));
        },
        // select2 wants a flat `{id: label}` object, where the sorted lists above are arrays of
        // `{id, label}` used for rendering elsewhere. Rebuilt from those rather than from the raw
        // props so both shapes stay derived from one place. `sortByText` on the field handles the
        // ordering, so key order here does not matter.
        scalingProfileOptions() {
            return Object.fromEntries(this.sortedScalingProfiles.map(({id, label}) => [id, label]));
        },
        alarmProfileOptions() {
            return Object.fromEntries(this.sortedAlarmProfiles.map(({id, label}) => [id, label]));
        },
        headers() {
            return [
                {name: 'Point', key: 'name', sortBy: 'name'},
                {name: 'Area / Zone', key: 'zone_id'},
                // Both sort on the profile's name server-side, not its id, so the order matches
                // the alphabetical dropdowns.
                {name: 'Scaling Profile', key: 'scaling_profile_id', sortBy: 'scaling_profile_id'},
                {name: 'Alarm Profile', key: 'selected_alarm_profile', sortBy: 'selected_alarm_profile'},
            ];
        },
        selectedIds() {
            return Object.keys(this.selected).filter(id => this.selected[id]);
        },
        selectedCount() {
            return this.selectedIds.length;
        },
        // Selection is scoped to the current page, so "all" means the rows on screen.
        hasActiveFilters() {
            return Object.values(this.filters).some(v => v !== '');
        },
        // Everything matching the filters is already ticked, so the button offers the inverse.
        allMatchingSelected() {
            const total = this.ajaxTable.pageInfo.total ?? 0;

            return total > 0 && this.selectedCount >= total;
        },
        selectAllLabel() {
            if (this.selectingAll) {
                return 'Selecting...';
            }

            return (this.allMatchingSelected || this.pageAllSelected) ? 'Select none' : 'Select all';
        },
        // The button only appears when it can do what it says: take on every point matching the
        // current filters. Above the cap it is hidden rather than quietly degrading to "the rows
        // on this page", which would be the same words meaning something much smaller.
        canSelectAllMatching() {
            const total = this.ajaxTable.pageInfo.total ?? 0;

            return total > 0 && total <= this.selectAllLimit;
        },
        selectAllCaption() {
            const total = this.ajaxTable.pageInfo.total ?? 0;

            return `${total.toLocaleString()} matching`;
        },
        pageAllSelected() {
            return this.data.length > 0 && this.data.every(row => this.selected[row.id]);
        },
        dirtyCount() {
            return Object.keys(this.dirty).length;
        },
        // Pending edits carry across filter changes, so they can outgrow what one save may write.
        // The server refuses the same number (MetersController::postEditMultiple); checking here
        // tells the user before the redirect, rather than after it discards their edits.
        overSaveLimit() {
            return this.dirtyCount > this.selectAllLimit;
        },
        // A point cannot be nameless. The server refuses the whole save if one is, so say so here
        // before the redirect discards the user's other edits.
        blankNameCount() {
            return Object.values(this.dirty)
                .filter(pending => Object.prototype.hasOwnProperty.call(pending, 'name')
                    && String(pending.name ?? '').trim() === '')
                .length;
        },
        // Points whose pending edits change a scaling or alarm profile. Both send a settings
        // downlink to the device, so they carry the most risk. Counted per point, matching
        // MetersController::highImpactPointCount.
        highImpactCount() {
            return Object.values(this.dirty)
                .filter(pending => Object.prototype.hasOwnProperty.call(pending, 'scaling_profile_id')
                    || Object.prototype.hasOwnProperty.call(pending, 'selected_alarm_profile'))
                .length;
        },
        needsTypedConfirm() {
            return this.highImpactCount > this.typedConfirmAbove;
        },
        reviewConfirmed() {
            return ! this.needsTypedConfirm || this.confirmCount.trim() === String(this.highImpactCount);
        },
        // One line per distinct change ("Scaling Profile -> Fahrenheit: 250 points"), riskiest
        // fields first. Renames are grouped as one line, since each has its own new value.
        reviewSummary() {
            const order = ['scaling_profile_id', 'selected_alarm_profile', 'zone_id', 'name'];
            const groups = {};

            Object.entries(this.dirty).forEach(([id, pending]) => {
                const original = this.originals[id] ?? {};

                Object.keys(pending).forEach(field => {
                    const to = field === 'name' ? null : this.displayValue(field, pending[field], original);
                    const key = `${field}|${to}`;

                    groups[key] ??= {
                        key,
                        field,
                        label: field === 'name' ? 'Renamed' : this.fieldLabels[field] ?? field,
                        to,
                        count: 0,
                    };
                    groups[key].count++;
                });
            });

            return Object.values(groups).sort((a, b) =>
                (order.indexOf(a.field) - order.indexOf(b.field)) || (b.count - a.count));
        },
        saveBlocked() {
            return this.dirtyCount === 0 || this.overSaveLimit || this.blankNameCount > 0;
        },
        // Only an alarm profile has a real "none": the server saves a cleared one as no profile.
        // A cleared zone would be ignored and a cleared scaling profile would become "No Scaling",
        // so offering Clear there would promise something the save does not do.
        pickerAllowsClear() {
            return this.pickerField === 'selected_alarm_profile';
        },
        saveLabel() {
            return this.saving ? 'Saving...' : 'Save Changes';
        },
        // Flattened before -> after list driving the review modal. Ids are resolved to the
        // same labels the user picked in the dropdowns so the summary is readable.
        reviewChanges() {
            return Object.entries(this.dirty).map(([id, pending]) => {
                const original = this.originals[id] ?? {};

                const fields = Object.keys(pending).map(field => ({
                    field,
                    label: this.fieldLabels[field] ?? field,
                    from: this.displayValue(field, original[field], original),
                    to: this.displayValue(field, pending[field], original),
                }));

                return {
                    id,
                    name: original.name ?? '',
                    serial: original.meter_serial ?? '',
                    fields,
                };
            });
        },
        // Total individual field edits, which can exceed the number of points changed.
        reviewFieldCount() {
            return this.reviewChanges.reduce((total, row) => total + row.fields.length, 0);
        },
        // The row whose cell is being picked, if any.
        pickerRow() {
            if (this.pickerRowId === null) {
                return null;
            }

            return this.data.find(r => String(r.id) === String(this.pickerRowId)) ?? null;
        },
        pickerTitle() {
            return {
                zone_id: 'Move to zone',
                scaling_profile_id: 'Set scaling profile',
                selected_alarm_profile: 'Set alarm profile',
            }[this.pickerField] ?? '';
        },
        pickerPlaceholder() {
            return {
                zone_id: 'Search zones...',
                scaling_profile_id: 'Search scaling profiles...',
                selected_alarm_profile: 'Search alarm profiles...',
            }[this.pickerField] ?? 'Type to filter...';
        },
        pickerSubtitle() {
            if (this.bulkMode) {
                return `Applies to ${this.selectedCount} selected point${this.selectedCount === 1 ? '' : 's'}`;
            }

            return this.pickerRow ? `${this.pickerRow.name} - ${this.pickerRow.meter_serial}` : '';
        },
        pickerCurrentValue() {
            // A bulk apply spans points with differing values, so there is no single "current".
            if (this.bulkMode || ! this.pickerRow || ! this.pickerField) {
                return null;
            }

            const value = this.valueFor(this.pickerRow, this.pickerField);

            return value === null || value === undefined ? '' : String(value);
        },
        // Flattened [{value, label, group?}] for whichever field is being picked.
        pickerOptions() {
            if (this.pickerField === 'zone_id') {
                return Object.entries(this.zoneSelectByAreas).flatMap(([areaName, zones]) =>
                    Object.entries(zones).map(([zoneId, zoneName]) => ({
                        value: String(zoneId),
                        label: zoneName,
                        group: areaName,
                    }))
                );
            }

            if (this.pickerField === 'scaling_profile_id') {
                return Object.entries(this.scalingProfiles)
                    .map(([id, name]) => ({value: String(id), label: name}))
                    .sort((a, b) => a.label.localeCompare(b.label));
            }

            if (this.pickerField === 'selected_alarm_profile') {
                // In bulk mode the shared public list is the only sensible set, since the ticked
                // points may sit on different private profiles.
                const options = (! this.bulkMode && this.pickerRow)
                    ? this.alarmProfileOptionsFor(this.pickerRow)
                    : this.alarmProfiles;

                // The whole option object rides along (thresholds + enabled flags) so the picker
                // can render the same chips as the grid. Zone and scaling options carry no such
                // fields, which is how the picker knows not to draw chips for them.
                return Object.entries(options)
                    .map(([id, option]) => ({...option, value: String(id)}))
                    .sort((a, b) => a.label.localeCompare(b.label));
            }

            return [];
        },
    },
    created() {
        // Plain instance fields rather than reactive data: these hold timer handles, which nothing
        // renders.
        this._copiedTimers = {};
    },
    mounted() {
        window.addEventListener('beforeunload', this.warnOnUnsavedChanges);
    },
    beforeUnmount() {
        window.removeEventListener('beforeunload', this.warnOnUnsavedChanges);
        Object.values(this._copiedTimers).forEach(clearTimeout);
    },
    methods: {
        async getData() {
            try {
                this.ajaxTable.loading = true;
                this.requestNumber++;

                const params = JSON.parse(JSON.stringify(this.ajaxTable));
                params.requestNumber = this.requestNumber;
                delete params.pageInfo;
                Object.assign(params, this.activeFilterParams());

                const response = await axios.get(
                    this.buildCompanyApiInternalRoute('meters/edit-multiple'),
                    {params: params}
                );

                // A slower earlier request must not overwrite a newer one's results.
                if (response.data?.meta?.requestNumber !== this.requestNumber) {
                    return;
                }

                this.alarmProfiles = response.data.alarmProfiles ?? {};
                this.data = response.data.data ?? [];
                this.loadedSearch = params.search ?? '';
                this.ajaxUpdateTableData(response.data);
                // A new page of rows means the previous tick marks refer to points that are no
                // longer on screen; drop them rather than let a hidden selection be applied.
                this.clearSelection();
            } catch (e) {
                console.error(e);
            } finally {
                this.ajaxTable.loading = false;
            }
        },

        changedFieldCount(row) {
            return Object.keys(this.dirty[row.id] ?? {}).length;
        },

        // Undo one field's edit without touching the row's other pending changes. Reuses setValue
        // so the "edited back to the original is not a change" logic drops the field (and the row,
        // if it was the last one) out of `dirty` on its own.
        revertField(row, field) {
            const original = this.originals[row.id];
            if (! original) {
                return;
            }

            this.setValue(row, field, original[field]);
        },

        // True when this specific field on this row has an unsaved edit. Drives the per-cell
        // "changed" styling so a glance at the grid shows both which rows will be written and
        // which values on them.
        isChanged(row, field) {
            return Object.prototype.hasOwnProperty.call(this.dirty[row.id] ?? {}, field);
        },

        // The value this field held before the user touched it, for the changed-cell tooltip.
        originalDisplayValue(row, field) {
            const original = this.originals[row.id];
            if (! original) {
                return null;
            }

            return this.displayValue(field, original[field], original);
        },

        // An unsaved edit takes precedence over the value loaded from the server.
        valueFor(row, field) {
            const pending = this.dirty[row.id];
            if (pending && Object.prototype.hasOwnProperty.call(pending, field)) {
                return pending[field];
            }

            return row[field];
        },

        setValue(row, field, value) {
            // Snapshot the server-side values the first time a point is touched. Rows are
            // discarded on paging, so without this the review modal could not show what a
            // value was changed *from* once the user has navigated away and back.
            if (! this.originals[row.id]) {
                this.originals = {...this.originals, [row.id]: {...row}};
            }

            const pending = {...(this.dirty[row.id] ?? {})};
            pending[field] = value;

            // Editing a field back to its original value is not a change. A name is compared (and
            // saved) trimmed, so stray whitespace alone never counts as a rename.
            const original = this.originals[row.id];
            const normalise = (v) => field === 'name' ? String(v ?? '').trim() : String(v ?? '');
            if (normalise(original[field]) === normalise(value)) {
                delete pending[field];
            }

            // The original is kept even when the point is no longer dirty. For a point off the
            // current page it is the only full copy of the row, and a later bulk action on the same
            // selection needs it (applyPick skips an id it cannot resolve to a row). Nothing is
            // written until save, so the snapshot cannot go stale before then.
            const dirty = {...this.dirty};
            if (Object.keys(pending).length === 0) {
                delete dirty[row.id];
            } else {
                dirty[row.id] = pending;
            }

            this.dirty = dirty;
        },

        alarmProfileOptionsFor(row) {
            return row.extra_alarm_profile
                ? {...this.alarmProfiles, ...row.extra_alarm_profile}
                : this.alarmProfiles;
        },

        // The {label, thresholds} object for a row's alarm profile, with a safe empty shape so
        // the template can render unconditionally.
        alarmOptionFor(row, value) {
            return this.alarmProfileOptionsFor(row ?? {})[value] ?? {label: '(none)', high: null, low: null};
        },

        // Whether the point resolves to a real alarm profile, as opposed to the `(none)` fallback
        // `alarmOptionFor` returns. `alarm_profile_id` is nullable and sites do leave points
        // without one.
        hasAlarmProfile(row) {
            const value = this.valueFor(row, 'selected_alarm_profile');

            if (value === null || value === undefined || value === '') {
                return false;
            }

            return this.alarmProfileOptionsFor(row)[value] !== undefined;
        },

        // Maps an API alarm-profile option onto AlarmThresholdChips' props. Every profile reports
        // its state, including one with no thresholds: "configured but switched off" and "no
        // alarms configured" are different situations on a screen that reassigns profiles in bulk.
        alarmChipProps(option) {
            return {
                high: option.high ?? null,
                low: option.low ?? null,
                thresholdAlarms: !! option.threshold_alarms_enabled,
                thresholdResolve: !! option.threshold_resolve_enabled,
                offlineAlarms: !! option.offline_alarms_enabled,
                offlineResolve: !! option.offline_resolve_enabled,
                hasAnyThreshold: !! option.has_any_threshold,
            };
        },

        // Turn a stored value into what the user actually sees in the dropdown, so the review
        // modal reads "Fahrenheit -> Celsius" rather than "4 -> 3".
        displayValue(field, value, row) {
            if (value === null || value === undefined || value === '') {
                return field === 'name' ? '(empty)' : '(none)';
            }

            if (field === 'zone_id') {
                for (const [areaName, zones] of Object.entries(this.zoneSelectByAreas)) {
                    if (Object.prototype.hasOwnProperty.call(zones, value)) {
                        return `${areaName} / ${zones[value]}`;
                    }
                }

                return String(value);
            }

            if (field === 'scaling_profile_id') {
                return this.scalingProfiles[value] ?? String(value);
            }

            if (field === 'selected_alarm_profile') {
                const option = this.alarmProfileOptionsFor(row ?? {})[value];
                if (! option) {
                    return String(value);
                }

                // Plain-text form, for places that cannot render the coloured threshold chips
                // (the review modal's from -> to column, the struck-through previous value).
                const parts = [];
                if (option.low !== null && option.low !== undefined) {
                    parts.push(`min ${option.low}`);
                }
                if (option.high !== null && option.high !== undefined) {
                    parts.push(`max ${option.high}`);
                }

                return parts.length ? `${option.label} (${parts.join(', ')})` : option.label;
            }

            return String(value);
        },

        discardChanges() {
            if (! window.confirm('Discard all unsaved changes?')) {
                return;
            }

            // Only the edits are dropped. `originals` stays, for the same reason setValue keeps a
            // snapshot once a point is no longer dirty: a "select all matching" selection survives
            // the discard, and its off-page ids resolve to rows only through `originals`. Clearing
            // it would make the next bulk apply silently reach just the points on this page.
            this.dirty = {};
        },

        // Maps a 1-based AjaxTable column slot to the field it renders.
        columnKey(slot) {
            return this.headers[slot - 1]?.key ?? null;
        },

        // Clicking anywhere in a row toggles its checkbox. The interactive cells (name input,
        // value pickers, the checkbox itself) stop propagation so they keep their own behaviour.
        onRowClicked(rowIndex) {
            const row = this.data[rowIndex];
            if (! row) {
                return;
            }

            this.toggleSelected(row, ! this.selected[row.id]);
        },

        // Trailing four characters, which is how a point is usually referred to out loud. Short
        // serials are returned whole rather than padded or blanked.
        lastFour(serial) {
            return String(serial ?? '').slice(-4);
        },

        async copySerial(row) {
            if (await this.writeToClipboard(row.meter_serial ?? '')) {
                this.acknowledgeCopy('copiedSerialId', row.id);
            }
        },

        async copyLastFour(row) {
            if (await this.writeToClipboard(this.lastFour(row.meter_serial))) {
                this.acknowledgeCopy('copiedLastFourId', row.id);
            }
        },

        // Shows the tick on whichever button was pressed, then clears it. Each button owns its own
        // timer so copying one does not cut short the other's acknowledgement.
        acknowledgeCopy(field, rowId) {
            this[field] = rowId;
            clearTimeout(this._copiedTimers[field]);
            this._copiedTimers[field] = setTimeout(() => {
                this[field] = null;
            }, 1500);
        },

        // Clipboard write, with a fallback for the cases the async API refuses: a non-secure
        // origin, or a document the browser does not consider focused. A rejection there falls
        // through to execCommand rather than leaving the click silently doing nothing.
        async writeToClipboard(text) {
            if (navigator.clipboard?.writeText) {
                try {
                    await navigator.clipboard.writeText(text);

                    return true;
                } catch (e) {
                    // Fall through to the scratch-field path below.
                }
            }

            return this.copyViaScratchField(text);
        },

        copyViaScratchField(text) {
            const scratch = document.createElement('textarea');
            scratch.value = text;
            scratch.setAttribute('readonly', '');
            scratch.style.position = 'fixed';
            scratch.style.top = '0';
            scratch.style.opacity = '0';
            document.body.appendChild(scratch);
            scratch.select();

            let copied = false;

            try {
                copied = document.execCommand('copy');
            } catch (e) {
                copied = false;
            }

            document.body.removeChild(scratch);

            return copied;
        },

        toggleSelected(row, isSelected) {
            const selected = {...this.selected};
            if (isSelected) {
                selected[row.id] = true;
            } else {
                delete selected[row.id];
            }

            this.selected = selected;
        },

        // Only the filters that are actually set, so an unset dropdown does not narrow anything.
        activeFilterParams() {
            return Object.fromEntries(
                Object.entries(this.filters).filter(([, value]) => value !== '')
            );
        },

        setFilter(key, value) {
            // select2 emits null when its selection is cleared, and jQuery's `.val()` can hand
            // back the string "null" for a cleared single select. Both mean "no filter", so they
            // are normalised to '' -- `activeFilterParams` drops only '', and anything else would
            // be posted to the server as a real filter value matching nothing.
            const normalised = (value === null || value === undefined || value === 'null') ? '' : String(value);

            // select2 fires a synthetic `change` at the end of every `buildSelect2()`, and it
            // rebuilds whenever its `options` prop changes. Because `getData()` refreshes
            // `alarmProfiles` (and so `alarmProfileOptions`) on every response, an unguarded
            // handler here is a feedback loop: mount/rebuild -> setFilter -> getData -> new
            // options -> rebuild -> ... which floods the endpoint and leaves the table flickering
            // between "Loading" and "No points found" as each response is discarded as stale.
            // Only a genuine change of value is a user action worth reloading for.
            if (this.filters[key] === normalised) {
                return;
            }

            this.filters = {...this.filters, [key]: normalised};
            this.ajaxTable.page = 1;
            // A selection made under the old filters would include points the user can no longer
            // see; applying a bulk action to those is exactly the surprise to avoid.
            this.clearSelection();
            this.getData();
        },

        clearFilters() {
            this.filters = {areaId: '', zoneId: '', scalingProfileId: '', alarmProfileId: ''};
            this.ajaxTable.page = 1;
            this.clearSelection();
            this.getData();
        },

        onSelectAllClicked() {
            if (this.allMatchingSelected || this.pageAllSelected) {
                this.clearSelection();

                return;
            }

            const total = this.ajaxTable.pageInfo.total ?? 0;

            // Everything matching already fits on screen, so no extra round trip is needed.
            if (total <= this.data.length) {
                this.selectPageOnly();

                return;
            }

            this.selectAllMatching();
        },

        selectPageOnly() {
            const selected = {...this.selected};
            this.data.forEach(row => {
                selected[row.id] = true;
            });

            this.selected = selected;
        },

        // Fetches every matching point before selecting it. The rows are stashed in `originals`
        // so a bulk apply can resolve an off-page id to a full row, and the review modal can show
        // what each value is changing from.
        async selectAllMatching() {
            this.selectingAll = true;
            const generation = this.selectionGeneration;

            try {
                const response = await axios.get(
                    this.buildCompanyApiInternalRoute('meters/edit-multiple/selectable'),
                    {params: {...this.activeFilterParams(), search: this.loadedSearch}}
                );

                // The filters, search or page changed while this was loading, and that already
                // cleared the selection. Applying this response would select points matching the
                // old criteria, none of which the user can now see.
                if (generation !== this.selectionGeneration) {
                    return;
                }

                // Adopt the server's cap so the button's availability can never disagree with what
                // the server will actually return.
                if (typeof response.data.limit === 'number') {
                    this.selectAllLimit = response.data.limit;
                }

                if (response.data.capped) {
                    this.matchingTotal = response.data.total;

                    return;
                }

                const rows = response.data.data ?? [];
                const selected = {...this.selected};
                const originals = {...this.originals};

                rows.forEach(row => {
                    selected[row.id] = true;
                    if (! originals[row.id]) {
                        originals[row.id] = {...row};
                    }
                });

                this.selected = selected;
                this.originals = originals;
            } catch (e) {
                console.error(e);
            } finally {
                this.selectingAll = false;
            }
        },

        clearSelection() {
            this.selected = {};
            this.selectionGeneration++;
        },

        // Bulk apply: the picker writes one value across every ticked point.
        // Dismissing the picker without choosing must not leave a bulk apply armed.
        onPickerOpenChange(isOpen) {
            this.showPicker = isOpen;
            if (! isOpen) {
                this.bulkMode = false;
            }
        },

        openBulkPicker(field) {
            if (this.selectedCount === 0) {
                return;
            }

            this.pickerField = field;
            this.pickerRowId = null;
            this.bulkMode = true;
            this.showPicker = true;
        },

        // The slot override owns the select, so the page-size change is applied here rather than
        // by AjaxTable's own items-per-page component.
        onItemsPerPageChange(value) {
            this.ajaxTable.itemsPerPage = value;
            this.ajaxTable.page = 1;
            this.getData();
        },

        setNameInput(id, el) {
            if (el) {
                this.nameInputs[id] = el;
            }
        },

        // Swap the display button for an input and put the caret in it, so the click that opens
        // the field is also the click that focuses it.
        startEditingName(row) {
            // The buttons are disabled for a view-only user too; this also covers any other caller.
            if (! this.canEdit) {
                return;
            }

            this.editingNameId = row.id;

            this.$nextTick(() => {
                const input = this.nameInputs[row.id];
                if (input) {
                    input.focus();
                    input.select();
                }
            });
        },

        // Closes the editor regardless of how the user left the field (blur, Enter or Esc). The
        // value is already committed on every keystroke, so this only swaps the display back.
        stopEditingName(id) {
            if (this.editingNameId === id) {
                this.editingNameId = null;
            }
        },

        openPicker(field, row) {
            if (! this.canEdit) {
                return;
            }

            this.pickerField = field;
            this.pickerRowId = row.id;
            this.showPicker = true;
        },

        // The picker commits on selection, so there is no separate apply step.
        applyPick(value) {
            if (! this.pickerField) {
                return;
            }

            if (this.bulkMode) {
                // Resolve each id to a full row. `setValue` snapshots the row as the point's
                // "original", which the review modal reads to show what each value changes from,
                // so a partial stub here would make the review lie.
                this.selectedIds.forEach(id => {
                    const row = this.data.find(r => String(r.id) === String(id)) ?? this.originals[id];
                    if (row) {
                        this.setValue(row, this.pickerField, value);
                    }
                });

                this.bulkMode = false;

                return;
            }

            if (this.pickerRow) {
                this.setValue(this.pickerRow, this.pickerField, value);
            }
        },

        // Saving is a bulk write across points that may not be on screen, so it goes through a
        // review step rather than committing straight from the footer.
        reviewChangesBeforeSave() {
            if (this.saveBlocked) {
                return;
            }

            this.confirmCount = '';
            this.showReview = true;
        },

        warnOnUnsavedChanges(e) {
            if (this.saving || this.dirtyCount === 0) {
                return;
            }

            e.preventDefault();
            e.returnValue = '';
        },

        /**
         * Posts only the changed points, in the `meter[id][field]` shape the existing
         * `postEditMultiple` handler already parses.
         */
        save() {
            if (this.saveBlocked || ! this.reviewConfirmed) {
                return;
            }

            this.saving = true;

            // Only the fields the user actually changed are posted, and the handler leaves any field
            // it does not receive untouched. Resending untouched fields "as they were" is what let a
            // rename re-save a point's other fields: a point whose scaling profile had been deleted
            // loads as null, went back as '', and was switched to "No Scaling" -- firing a scaling
            // downlink that the review modal never listed.
            const params = new URLSearchParams();
            Object.entries(this.dirty).forEach(([id, pending]) => {
                Object.entries(pending).forEach(([field, value]) => {
                    const param = this.saveParamNames[field];
                    if (! param) {
                        return;
                    }

                    const posted = field === 'name' ? String(value ?? '').trim() : (value ?? '');
                    params.append(`meter[${id}][${param}]`, posted);
                });
            });

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = this.postUrl;
            form.appendChild(this.hiddenInput('_token', this.csrf));
            form.appendChild(this.hiddenInput('data', params.toString()));
            form.appendChild(this.hiddenInput('confirm_count', this.confirmCount.trim()));
            document.body.appendChild(form);
            form.submit();
        },

        hiddenInput(name, value) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;

            return input;
        },
    },
};
</script>
